"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RecentRequestsFeed() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const components = [{
    id: "intake",
    label: "Intake",
    number: "01",
    name: "Initial Request",
    description: "Our streamlined intake process captures your core requirements immediately. We prioritize clarity and speed to ensure your project goals are aligned from the very first interaction.",
    specs: [{
      label: "Status",
      value: "Verified"
    }, {
      label: "Priority",
      value: "High"
    }, {
      label: "Format",
      value: "Digital"
    }],
    top: "32%",
    left: "82%"
  }, {
    id: "analysis",
    label: "Analysis",
    number: "02",
    name: "Strategic Review",
    description: "Deep-dive analysis of your specific needs. Our team evaluates technical feasibility and resource allocation to provide a roadmap that guarantees successful delivery.",
    specs: [{
      label: "Depth",
      value: "Advanced"
    }, {
      label: "Duration",
      value: "24h"
    }, {
      label: "Output",
      value: "Report"
    }],
    top: "42%",
    left: "48%"
  }, {
    id: "planning",
    label: "Planning",
    number: "03",
    name: "Resource Mapping",
    description: "We map your request to the optimal team members. By matching expertise with project complexity, we ensure that every milestone is met with precision and professional excellence.",
    specs: [{
      label: "Team",
      value: "Expert"
    }, {
      label: "Scale",
      value: "Custom"
    }, {
      label: "Method",
      value: "Agile"
    }],
    top: "55%",
    left: "38%"
  }, {
    id: "execution",
    label: "Execution",
    number: "04",
    name: "Active Development",
    description: "The core phase where your vision becomes reality. Our developers and designers work in tandem, maintaining constant communication to ensure the final product exceeds expectations.",
    specs: [{
      label: "Phase",
      value: "Active"
    }, {
      label: "Progress",
      value: "Tracking"
    }, {
      label: "Quality",
      value: "Premium"
    }],
    top: "68%",
    left: "62%"
  }, {
    id: "delivery",
    label: "Delivery",
    number: "05",
    name: "Final Handover",
    description: "Rigorous quality assurance testing followed by a seamless deployment. We provide full documentation and support to ensure your team is ready to hit the ground running.",
    specs: [{
      label: "Testing",
      value: "Complete"
    }, {
      label: "Support",
      value: "Included"
    }, {
      label: "Status",
      value: "Ready"
    }],
    top: "84%",
    left: "28%"
  }];
  const [activeId, setActiveId] = useState<string>("analysis");
  const active = components.find(c => c.id === activeId) ?? components[1];
  return <section data-section-id="5188" id="recent-requests-feed" className="bg-[var(--color-background)] text-[var(--color-foreground)] relative overflow-hidden py-20 lg:py-32">
      <div ref={ref} className={`mx-auto max-w-[1400px] px-6 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="relative lg:col-span-7 lg:-ml-6">
            <div className="relative w-full h-[60vh] sm:h-[75vh] lg:h-[90vh] overflow-hidden rounded-lg shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/project_workflow_dashboard.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/project_workflow_dashboard.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/project_workflow_dashboard.webp-wvc-sizes" alt="Professional project workflow dashboard" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[var(--color-background)]/40" />

              <div className="hidden lg:block">
                {components.map((c, i) => <button key={c.id} data-index={i} onMouseEnter={() => setActiveId(c.id)} onFocus={() => setActiveId(c.id)} onClick={() => setActiveId(c.id)} aria-label={`View details for ${c.label}`} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{
                top: c.top,
                left: c.left
              }}>
                    <span className={`absolute inset-0 -m-3 rounded-lg ${activeId === c.id ? "animate-ping bg-[var(--color-primary-500)]/30" : "bg-[var(--color-primary-500)]/10 animate-pulse"}`} style={{
                  borderRadius: "9999px"
                }} />
                    <span className={`relative block size-3 rounded-lg border transition-all duration-300 ${activeId === c.id ? "bg-[var(--color-primary-500)] border-[var(--color-primary-500)] scale-125" : "bg-[var(--color-primary-500)]/60 border-[var(--color-primary-500)]/80 group-hover:scale-110"}`} style={{
                  borderRadius: "9999px"
                }} />
                  </button>)}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 lg:pt-8">
            <div className="font-default text-xs uppercase tracking-widest text-[var(--color-primary-500)] mb-6">
              Workflow / 03
            </div>

            <h2 className="font-default text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-[var(--color-foreground)] mb-10">
              The <em className="italic font-light">Request</em>
              <br />
              Pipeline
            </h2>

            <p className="font-default text-base text-[var(--color-primary-700)] leading-relaxed max-w-md mb-12">
              Every client request follows a rigorous path of excellence. Explore our 
              operational stages to see how we transform your ideas into tangible results.
            </p>

            <div key={active.id} className="relative border border-[var(--color-primary-200)] p-8 lg:p-10 transition-opacity duration-400 animate-in fade-in rounded-lg shadow-md bg-white">
              <div className="absolute -top-3 left-8 bg-[var(--color-background)] px-3 font-default text-[10px] uppercase tracking-[0.25em] text-[var(--color-primary-500)]">
                {active.label} / {active.number}
              </div>

              <h3 className="font-default text-3xl lg:text-4xl text-[var(--color-foreground)] mb-4 leading-tight">
                {active.name}
              </h3>

              <p className="font-default text-sm text-[var(--color-primary-700)] leading-relaxed mb-8">
                {active.description}
              </p>

              <div className="border border-[var(--color-primary-200)] bg-[var(--color-primary-100)]/40 p-5 mb-8 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-default text-[10px] uppercase tracking-widest text-[var(--color-primary-500)]">
                    Fig. {active.number}
                  </span>
                  <Plus className="size-3 text-[var(--color-primary-500)]" />
                </div>
                <div className="relative h-24 flex items-center justify-center">
                  <div className="absolute inset-x-0 top-1/2 h-px bg-[var(--color-primary-200)]" />
                  <div className="absolute inset-y-0 left-1/2 w-px bg-[var(--color-primary-200)]" />
                  <div className="relative size-16 border border-[var(--color-primary-500)]/60 rounded-lg flex items-center justify-center">
                    <div className="size-8 border border-[var(--color-primary-500)]/40 rounded-lg" />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {active.specs.map((s, i) => <div key={s.label} data-index={i} className="flex items-baseline justify-between border-b border-[var(--color-primary-200)]/50 pb-3 last:border-b-0">
                    <span className="font-default text-[11px] uppercase tracking-widest text-[var(--color-primary-500)]">
                      {s.label}
                    </span>
                    <span className="font-default text-sm tabular-nums text-[var(--color-foreground)]">
                      {s.value}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        <div className="hidden lg:grid grid-cols-5 mt-20 border-t border-[var(--color-primary-200)] pt-8">
          {components.map((c, i) => <button key={c.id} data-index={i} onMouseEnter={() => setActiveId(c.id)} onFocus={() => setActiveId(c.id)} onClick={() => setActiveId(c.id)} className={`group text-left transition-colors duration-300 ${activeId === c.id ? "text-[var(--color-primary-500)]" : "text-[var(--color-primary-400)] hover:text-[var(--color-foreground)]"}`}>
              <div className="font-default text-[10px] uppercase tracking-[0.3em] mb-2">
                {c.number}
              </div>
              <div className="font-default text-xs uppercase tracking-widest">
                {c.label}
              </div>
              <div className={`mt-3 h-px transition-all duration-500 ${activeId === c.id ? "bg-[var(--color-primary-500)] w-full" : "bg-[var(--color-primary-200)] w-6 group-hover:w-full group-hover:bg-[var(--color-foreground)]/40"}`} />
            </button>)}
        </div>

        <div className="lg:hidden mt-10 -mx-6 px-6 overflow-x-auto">
          <div className="flex gap-3 pb-2 w-max">
            {components.map((c, i) => <button key={c.id} data-index={i} onClick={() => setActiveId(c.id)} className={`shrink-0 border px-4 py-3 text-left transition-colors duration-300 rounded-lg ${activeId === c.id ? "border-[var(--color-primary-500)] text-[var(--color-primary-500)] bg-[var(--color-primary-500)]/5" : "border-[var(--color-primary-200)] text-[var(--color-primary-500)]"}`}>
                <div className="font-default text-[10px] uppercase tracking-[0.25em] mb-1">
                  {c.number}
                </div>
                <div className="font-default text-xs uppercase tracking-widest">
                  {c.label}
                </div>
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}